<script setup lang="ts">
import { isElementInViewport } from '@@/bits/dom'
import environment from '@@/bits/environment'
import { tailwindSurfaceFontClass } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import sectionSorter from '@@/bits/section_sorter'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import type { PostDragEvent } from '@@/bits/surface_sortable'
import { getSortableOptions } from '@@/bits/surface_sortable'
import { defineAsyncComponent } from '@@/bits/vue'
import { WallWishGroupByTypes } from '@@/enums'
import type { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Cid, Section } from '@@/types'
import SurfaceMapPostListItem from '@@/vuecomponents/SurfaceMapPostListItem.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import { useSurfacePostsDragSortableMap } from '@@/vuecomposables/surface_posts_drag_sortable_map'
import { useSurfaceSectionsDragSortableMap } from '@@/vuecomposables/surface_sections_drag_sortable_map'
import type { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import vEmitLongPress from '@@/vuedirectives/emit_long_press'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance } from 'vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import Draggable from 'vuedraggable'

const SurfaceMapPostListItemEmpty = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceMapPostListItemEmpty.vue'),
)

const props = withDefaults(
  defineProps<{
    isShown: boolean
    boxColor?: OzBoxColors
  }>(),
  {
    isShown: false,
    boxColor: undefined,
  },
)

const emit = defineEmits<{
  (name: 'start-reordering'): void
  (name: 'stop-reordering'): void
}>()

const surfaceMapStore = useSurfaceMapStore()
const surfacePostsStore = useSurfacePostsStore()
const surfaceSectionsStore = useSurfaceSectionsStore()

const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { shouldDisableDraggingPost } = storeToRefs(useSurfacePostsDragAndDropStore())
const { isASectionDragging } = storeToRefs(surfaceSectionsStore)
const { sortedGroups, wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())
const { colorScheme, dir, fontId, isSubmissionRequest } = storeToRefs(useSurfaceStore())
const { windowWidth } = storeToRefs(useWindowSizeStore())

const { shouldDisplayPost } = surfacePostsStore

const fontClass = computed(() => tailwindSurfaceFontClass(fontId.value))

const internalPostsByGroup = ref(allSortedPostsByGroup.value)
const isDragging = ref(false)

const sortedGroupsToShow = computed(() => {
  if (isSubmissionRequest.value) return []
  return sortedGroups.value.map((group) => ({
    ...group,
    ariaLabelledby: `group-title-${group.id}`,
  }))
})

const arePostsInGroupHidden = (groupId: Group['id']): boolean => {
  const posts = internalPostsByGroup.value[groupId]
  return posts == null || posts.every((post) => !shouldDisplayPost(post.cid))
}

const isGroupEmpty = (group: Group) => (internalPostsByGroup.value[group.id] || []).length === 0
const shouldShowDivider = (group: Group, index: number) => {
  const isLastPostInGroup = index === (internalPostsByGroup.value[group.id] || []).length - 1
  if (isLastPostInGroup) return false
  return wishGroupBy.value !== WallWishGroupByTypes.None
}

function forceSyncInternalPostsWithServer(): void {
  nextTick(() => {
    // Re-sync post array with server
    internalPostsByGroup.value = allSortedPostsByGroup.value
  })
}

watch(allSortedPostsByGroup, (newPostsByGroup) => {
  // `postsByGroup` can change in real-time when someone else adds or
  // deletes a post. Since we use an internal array to render the posts,
  // we need to sync it with `postsByGroup`. But this sync should only
  // happen when the user is not reordering the posts. Otherwise, drag
  // and drop will be messy.
  if (!isDragging.value) internalPostsByGroup.value = newPostsByGroup
})

function onGroupHeaderLongPress(e: LongPressEvent, group: Group): void {
  if (isASectionDragging.value) {
    e.preventDefault()
    return
  }
  if (wishGroupBy.value === WallWishGroupByTypes.Section) {
    e.preventDefault()
    surfaceMapStore.startPickingLocation({ sectionId: (group as Section).id })
  }
}

/**
 * REORDERING SECTIONS
 */

// VueDraggable passes these options to SortableJS under the hood.
const sectionDraggableOptions = {
  ...getSortableOptions('section', 'map'),
  group: 'section-sortable-group',
  ghostClass: 'sortable-indicator-line',
  dragClass: 'sortable-dragging-clone',
  draggable: '.surface-section',
  handle: '.surface-group-title',
  filter: '.no-drag',
  preventOnFilter: false,
}

const { onSectionDragClone, onSectionDragStart, onSectionDragEnd } = useSurfaceSectionsDragSortableMap({
  onDragStart: () => {
    isASectionDragging.value = true
  },
  onDragEnd: (e) => {
    isASectionDragging.value = false
    updateLeftmostGroupInViewportIndex()
    const { groupId } = e
    if (groupId) {
      const sectionElement = document.querySelector(`#section-${groupId}`) as HTMLElement | undefined
      if (!sectionElement) return
      const sortResult = sectionSorter.getNewIndex(sectionElement)
      if (!sortResult) return
      surfaceSectionsStore.sortSection(sortResult)
    }
  },
})

/**
 * REORDERING POSTS
 */

// VueDraggable passes these options to SortableJS under the hood.
// https://github.com/SortableJS/Sortable#options
const draggableOptions = {
  ...getSortableOptions('post', 'map'),
  group: 'sortable-group',
}

const postSorter = usePostSorter()

function reorder({ postCid, groupId, newIndex }: { postCid: Cid; groupId: Group['id']; newIndex: number }): void {
  const postsInGroup = internalPostsByGroup.value[groupId] || []
  const prevPost = postsInGroup[newIndex - 1] || null
  const nextPost = postsInGroup[newIndex + 1] || null
  const { sortIndex, gapSize } = postSorter.getNewIndexBetween(prevPost, nextPost)
  const sectionId =
    wishGroupBy.value === WallWishGroupByTypes.Section
      ? typeof groupId === 'string'
        ? parseInt(groupId)
        : groupId
      : null
  // Update new post order to server
  surfacePostsStore.sortPost({
    postCid,
    sortIndex,
    sectionId,
    gapSize,
  })
}

const { onPostDragStart, onPostDragMove, onPostDragEnd } = useSurfacePostsDragSortableMap({
  onDragStart: () => {
    isDragging.value = true
    emit('start-reordering')
  },
  onDragEnd: (e: PostDragEvent) => {
    isDragging.value = false
    emit('stop-reordering')
    reorder({
      postCid: e.postCid!,
      groupId: e.groupId!,
      newIndex: e.newIndex!,
    })
    forceSyncInternalPostsWithServer()
  },
})

/**
 * HOVERING ON POSTS
 * We highlight the post marker when the user hovers over a post.
 */

function onPostHover(postCid: Cid): void {
  if (!isDragging.value) surfaceMapStore.highlightPostMarker({ postCid })
}

function onPostUnhover(): void {
  surfaceMapStore.highlightPostMarker({ postCid: null })
}

function onPostClick(postCid: Cid): void {
  surfaceMapStore.openPostPopup({ postCid })
  // auto focus on marker popup content if open post popup
  // we need the timeout of 100ms for the popup content to be populated in the popup
  setTimeout(() => {
    const googleMarkerPopup = document.querySelector('#surface-map-google-marker-popup-content')
    if (googleMarkerPopup != null) {
      const el = googleMarkerPopup as HTMLElement
      el.focus()
    }
  }, 100)
}

/**
 * We only flip neverShown to false for the first time
 * the post list is opened.
 */
const neverShown = ref(true)
const stopWatchingIsShown = watch(
  () => props.isShown,
  (isShown) => {
    if (neverShown.value && isShown) {
      neverShown.value = false
      setTimeout(() => stopWatchingIsShown(), 0)
    }
  },
  {
    immediate: true,
  },
)

/**
 * STICKY HEADER
 * See this video to get an idea what it should look like:
 * https://linear.app/padlet/issue/PRO-4553#comment-a5e46136
 *
 * To create the sticky header effect, we render 3 headers for each section, but only one of them is
 * visible at a time:
 * - Left header (the real header): sticks to the left side of the group.
 * - Right header: sticks to the right side of the group.
 * - Sticky header: sticks to the left edge of the viewport.
 *
 * What the code below does is just to figure out when to show which header. Since the post list is
 * scrolled horizontally, and we want to stick the header to the left edge of the viewport, we need
 * to keep track of what the leftmost group in the viewport is. We do that by listening to the scroll
 * event and store the index of the leftmost group in `leftmostGroupInViewportIndex`.
 *
 * Next, we observe the position of the left and right headers in the viewport to determine when to
 * show the which header. We only observe the headers in the leftmost group, thanks to the index we
 * store earlier. The sticky header is hidden by default, while the right header has to be rendered
 * so that we can use IntersectionObserver, but we set its `visibility` to `hidden`. Based on the
 * video, the rules for showing the headers are:
 * - When scrolling right to left:
 *   - Show the sticky header it when the left header starts to move out of the viewport. This creates
 *     the sticky effect.
 *   - Hide the sticky header and show the right header when their positions match, meaning the right
 *     header's left reaches 0. This creates the effect of the right header sliding away as we scroll.
 *   - When the group is completely scrolled away, our observers switch to the next group. Everything
 *     starts over again.
 * - When scrolling left to right:
 *   - Don't show the sticky header until the right header is completely inside the viewport. After
 *     that, the sticky header takes over (hide the right header).
 *   - When the left header is completely inside the viewport, hide the sticky header.
 *
 * We can simplify the logic above by:
 * - xRightHeader = rightHeader.left < 0
 * - xStickyHeader = !leftHeaderFullyInsideViewport && !xRightHeader
 *
 * That's basically how we implement the sticky header effect. Smaller details are commented inline.
 * Feel free to read :)
 *
 * Note: This solution works perfectly in RTL. However, to simplify things, we still use `left` and
 * `right` instead of `start` and `end` for variable names (e.g `startmost` doesn't sound natural).
 */
const surfaceMapPostListEl = ref<ComponentPublicInstance | null>(null)
const stickyHeaderTop = computed(() => {
  const el = surfaceMapPostListEl.value?.$el
  if (!el) return 0
  return el.getBoundingClientRect().top
})
const leftHeaderFullyInsideViewport = ref(true)
const leftmostGroupInViewportIndex = ref(0)
const xRightHeader = ref(false)
const xStickyHeader = computed(() => !leftHeaderFullyInsideViewport.value && !xRightHeader.value)

watch(
  () => props.isShown,
  (isShown) => {
    // Reset all stateful values when the post list is closed.
    if (!isShown) {
      leftHeaderFullyInsideViewport.value = true
      leftmostGroupInViewportIndex.value = 0
      xRightHeader.value = false
    }
  },
)

/**
 * DOM functions
 */
function findAllLeftHeaderElements(): NodeListOf<HTMLElement> {
  return document.querySelectorAll(
    'header:not(.surface-group-sticky-header):not(.surface-group-right-header) .surface-group-title',
  )
}

function fightAllRightHeaderElements(): NodeListOf<HTMLElement> {
  return document.querySelectorAll('header.surface-group-right-header .surface-group-title')
}

function findAllGroupElements(): NodeListOf<HTMLElement> {
  return document.querySelectorAll('section.surface-group')
}

function findIndexLeftmostGroupInViewport(): number {
  const groupEls = findAllGroupElements()
  for (let i = 0; i < groupEls.length; i++) {
    const el = groupEls[i]
    if (isElementInViewport(el)) return i
  }
  return -1
}

function updateLeftmostGroupInViewportIndex(): void {
  // Update the leftmost group in viewport index every time we scroll.
  // TODO: Find an alternative way since handling every scroll event is expensive.
  leftmostGroupInViewportIndex.value = findIndexLeftmostGroupInViewport()
}

/**
 * Use IntersectionObserver to toggle headers' visibility.
 */

let leftHeaderObserver: IntersectionObserver | null = null
let rightHeaderObserver: IntersectionObserver | null = null

function initLeftHeaderObserver(): void {
  leftHeaderObserver = new IntersectionObserver(
    (entries) => {
      leftHeaderFullyInsideViewport.value = entries[0].isIntersecting
    },
    {
      // Threshold = 1 means the element has to be fully inside the viewport
      // for isIntersecting to be true.
      threshold: 1,
    },
  )
}

function initRightHeaderObserver(): void {
  rightHeaderObserver = new IntersectionObserver(
    (entries) => {
      let headerRect = { left: 0, right: 0, top: 0, bottom: 0 }
      for (const entry of entries) {
        const { left, right, top, bottom } = entry.boundingClientRect
        if (left !== 0 || right !== 0 || top !== 0 || bottom !== 0) {
          headerRect = entry.boundingClientRect
          break
        }
      }
      xRightHeader.value = dir.value === 'ltr' ? headerRect.left < 0 : headerRect.right > windowWidth.value
    },
    {
      threshold: 1,
    },
  )
}

function observeHeaders(index: number): void {
  leftHeaderObserver?.disconnect()
  rightHeaderObserver?.disconnect()

  const leftHeader = findAllLeftHeaderElements()[index]
  const rightHeader = fightAllRightHeaderElements()[index]

  if (leftHeader) leftHeaderObserver?.observe(leftHeader)
  if (rightHeader) rightHeaderObserver?.observe(rightHeader)
}

watch(leftmostGroupInViewportIndex, (newIndex: number, oldIndex: number) => {
  if (newIndex !== oldIndex) {
    // Set these to true right away to avoid the header flashing bug. Otherwise,
    // these variables are only updated by the next time we scroll and the new
    // IntersectionObserver callbacks are called.
    leftHeaderFullyInsideViewport.value = true
    xRightHeader.value = false

    observeHeaders(newIndex)
  }
})

watch(sortedGroups, (newSortedGroups, oldSortedGroups) => {
  if (newSortedGroups.length !== oldSortedGroups.length) {
    nextTick(() => {
      // Changes in the number of groups can change the leftmost group in viewport.
      leftmostGroupInViewportIndex.value = findIndexLeftmostGroupInViewport()
      observeHeaders(leftmostGroupInViewportIndex.value)
    })
  }
})

function onAfterPostListOpenTransition(): void {
  observeHeaders(0)
}

function onAfterPostListCloseTransition(): void {
  leftHeaderObserver?.disconnect()
  rightHeaderObserver?.disconnect()
}

/**
 * LIFE CYCLE
 */
onMounted(() => {
  initLeftHeaderObserver()
  initRightHeaderObserver()

  if (environment === 'development') {
    // This calculation is not needed for the first load.
    // But we need it on hot reload (on dev). Otherwise
    // the top value will be set to the default value 0.
    requestAnimationFrame(onAfterPostListOpenTransition)
  }
})
</script>

<template>
  <section :aria-label="__('List of locations on this padlet')">
    <Transition name="slide" @after-enter="onAfterPostListOpenTransition" @after-leave="onAfterPostListCloseTransition">
      <!-- N.B v-if is to load the panel for the first time, after that we use v-show so that
    whenever user open the panel, we don't have to load all the attachment previews
    again (which requires hitting display attributes endpoint for each post with attachment) -->
      <Draggable
        v-if="!neverShown"
        v-show="isShown"
        id="surface-map-post-list"
        ref="surfaceMapPostListEl"
        :class="[
          'flex flex-row space-s-3',
          'overflow-y-auto w-full',
          {
            'scrollbar-photo': colorScheme === 'light',
            'scrollbar-photo-dark': colorScheme === 'dark',
          },
          fontClass,
        ]"
        :disabled="!canIDragSections"
        v-bind="sectionDraggableOptions"
        @clone="onSectionDragClone"
        @end="onSectionDragEnd"
        @start="onSectionDragStart"
        @scroll="
          wishGroupBy !== WallWishGroupByTypes.None && !isASectionDragging && updateLeftmostGroupInViewportIndex()
        "
      >
        <!-- STICKY HEADER -->
        <SurfacePostGroupHeaderSection
          v-if="wishGroupBy === WallWishGroupByTypes.Section && xStickyHeader"
          v-emit-long-press
          :section="sortedGroups[leftmostGroupInViewportIndex]"
          :class="[
            'fixed z-surface-map-control start-0',
            // Section header can only go as wide as a post width
            // minus 12px padding on the left and 16px padding on the right
            'max-w-[calc(304px-12px-16px)] overflow-hidden',
            'surface-group-sticky-header',
            'no-drag',
          ]"
          :style="{
            top: `${stickyHeaderTop}px`,
          }"
          @long-press="onGroupHeaderLongPress($event, sortedGroups[leftmostGroupInViewportIndex])"
        />

        <section
          v-for="(group, groupIndex) in sortedGroupsToShow"
          :id="`section-${group.id}`"
          :key="group.id"
          :data-id="group.id"
          :data-rank="group.sort_index"
          :class="{
            // Needs !important to override space-s-3
            'first:ms-3 last:!me-3': true,
            'surface-group': true,
            'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
          }"
          :aria-labelledby="group.ariaLabelledby"
        >
          <!-- GROUP HEADER -->
          <div v-if="wishGroupBy === WallWishGroupByTypes.Section" class="relative px-3">
            <!-- LEFT HEADER -->
            <SurfacePostGroupHeaderSection
              v-emit-long-press
              :section="group"
              :class="[
                // Section header can only go as wide as a post width
                // minus 12px padding on the left and 16px padding on the right
                'max-w-[calc(304px-12px-16px)]',
              ]"
              :use-default-bg-color="false"
              @long-press="onGroupHeaderLongPress($event, group)"
            />

            <!-- RIGHT HEADER -->
            <SurfacePostGroupHeaderSection
              v-emit-long-press
              :section="group"
              :class="[
                'absolute top-0 end-0',
                (!xRightHeader || groupIndex !== leftmostGroupInViewportIndex) && 'invisible',
                // Section header can only go as wide as a post width
                // minus 12px padding on the left and 16px padding on the right
                'max-w-[calc(304px-12px-16px)]',
                'surface-group-right-header',
                'no-drag',
              ]"
              :use-default-bg-color="false"
              @long-press="onGroupHeaderLongPress($event, group)"
            />
          </div>

          <!-- POSTS IN GROUP -->
          <Draggable
            :class="[
              'flex',
              'flex-row',
              wishGroupBy === WallWishGroupByTypes.None && 'space-s-2',
              'overflow-hidden rounded-xl w-full',
              'select-none',
              isDragging && {
                'w-[304px] h-[100px]': isGroupEmpty(group),
                'min-w-[304px]': true,
              },
              'group-posts',
            ]"
            :data-group-id="group.id"
            :list="internalPostsByGroup[group.id]"
            :disabled="shouldDisableDraggingPost || (isGroupEmpty(group) && !isDragging)"
            v-bind="draggableOptions"
            :move="onPostDragMove"
            @start="onPostDragStart"
            @end="onPostDragEnd"
          >
            <SurfaceMapPostListItem
              v-for="(post, index) in internalPostsByGroup[group.id]"
              v-show="shouldDisplayPost(post.cid)"
              :id="`wish-preview-${post.id}`"
              :key="post.cid"
              :class="['flex flex-none', 'w-fit']"
              :post="post"
              :box-color="boxColor"
              :font-class="fontClass"
              :border-radius="wishGroupBy === WallWishGroupByTypes.None ? 12 : 0"
              :x-divider="shouldShowDivider(group, index)"
              @post-click="onPostClick"
              @post-hover="onPostHover"
              @post-unhover="onPostUnhover"
            />
            <SurfaceMapPostListItemEmpty
              v-if="(isGroupEmpty(group) && !isDragging) || arePostsInGroupHidden(group.id)"
              class="flex-none w-[304px] h-[100px]"
              :box-color="boxColor"
              :border-radius="12"
            />
          </Draggable>
        </section>
      </Draggable>
    </Transition>
  </section>
</template>

<style lang="scss" scoped>
@import '@assets/stylesheets/3/modules/all';

.slide-enter,
.slide-leave-to {
  transform: translateY(100%);
}
.slide-enter-to,
.slide-leave {
  transform: translateY(0);
}

.slide-enter-active {
  transition: transform 0.25s cubic-bezier(0, 0, 0.2, 1); // https://material.io/design/motion/speed.html#easing
}
.slide-leave-active {
  transition: transform 0.15s cubic-bezier(0.4, 0, 1, 1); // https://material.io/design/motion/speed.html#easing
}
</style>
